'use client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AttachFilesRequestBody,
  FetchInsurerMinimalClaimDetailResponseBody,
  ListFilesRequestQuery,
  ListFilesResponseBody,
  OrderRelationTypeMetadata,
  TokenizeFileRequestBody,
  TokenizeFileResponseBody,
} from 'bff';
import { UserType } from 'database';
import debounce from 'lodash.debounce';
import React, { useCallback, useState, useMemo } from 'react';
import {
  FileCardList,
  FileRequestMessage,
  SINGLE_FILE_QUERY_KEY,
} from 'shared-components';
import {
  Button,
  ComboboxOption,
  Input,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from 'ui';
import { filterDocumentsByVisibility, localeDateFormat } from 'utils';
import Link from 'next/link';
import { DocumentTagsFilter, SearchTagsFn } from './DocumentTagsFilter';
import {
  RetrievePendingFileRequestResponseBody,
  VisibilityCategories,
  FileRequestWithRelations,
} from 'bff';
import { UploadFileCustom } from './UploadFileCustom';
import { DocumentsEmptyState } from './DocumentsEmptyState';
import MemoizedFileCard from './MemoizedFileCard';
import { IconX } from '@tabler/icons-react';
import { format } from 'date-fns';
import { useTranslations } from 'i11n';

export const DOCUMENTATION_FILES_QUERY_KEY = 'documentation-files';

export const buildClaimPendingFileRequestQueryKey = (claimId: string) =>
  `claim-${claimId}-pending-file-request`;

export type ListFilesFN = (input: {
  query?: ListFilesRequestQuery;
  cookie?: string;
}) => Promise<ListFilesResponseBody>;

type File = ListFilesResponseBody['files'][number] & {
  _version?: number;
};

type ListPendingFileRequestFN = (
  params: OrderRelationTypeMetadata,
  cookie?: string,
) => Promise<RetrievePendingFileRequestResponseBody>;

export type DocumentsFileExplorerProps = {
  initialData: ListFilesResponseBody;
  searchTags?: SearchTagsFn;
  listFiles: ListFilesFN;
  listPendingFileRequest: ListPendingFileRequestFN;
  onFilterChange?: (filters: { selectedTags: ComboboxOption[] }) => void;
  onSearch?: (search: string) => void;
  onOpenFile?: (file: File) => void;
  withFileRequestButton?: boolean;
  title?: string;
  docQueryKey?: string;
  claim?: FetchInsurerMinimalClaimDetailResponseBody;
  // @ts-ignore
  initialPendingFileRequests?: RetrievePendingFileRequestResponseBody;
  tokenizeFile?: (
    input: TokenizeFileRequestBody,
  ) => Promise<TokenizeFileResponseBody>;
  addUntaggedFiles: (files: AttachFilesRequestBody['files']) => Promise<any>;
  visibility?: Partial<VisibilityCategories>;
  showPlaceholder?: boolean;
  userType?: UserType;
  initialGlobalFilter?: string;
  initialSelectedTags?: ComboboxOption[];
  hideSearch?: boolean;
  onCancelPlaceholder?: (placeholder: {
    id: string;
    fileRequestId: string;
    tagName: string;
  }) => void;
} & Pick<ListFilesRequestQuery, 'scope_id' | 'scope_type'>;

export const DocumentsFileExplorer = ({
  initialData,
  scope_id,
  scope_type,
  searchTags = async () => ({ options: [], tags: [] }),
  listFiles,
  onFilterChange,
  onSearch,
  onOpenFile,
  withFileRequestButton = true,
  title,
  claim,
  docQueryKey,
  visibility = { tramitation: true, owner: true },
  initialPendingFileRequests,
  tokenizeFile,
  addUntaggedFiles,
  showPlaceholder = true,
  userType,
  listPendingFileRequest,
  initialGlobalFilter,
  initialSelectedTags,
  hideSearch = false,
  onCancelPlaceholder,
}: DocumentsFileExplorerProps) => {
  const t = useTranslations('fileRequest');
  const [selectedTags, setSelectedTags] = useState<ComboboxOption[]>(
    initialSelectedTags || [],
  );
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter || '');
  const [isRefetching, setIsRefetching] = useState(false);
  const queryClient = useQueryClient();

  const filesQuery = useQuery({
    queryKey: [
      DOCUMENTATION_FILES_QUERY_KEY,
      docQueryKey,
      scope_id,
      JSON.stringify(selectedTags.map((v) => v.value)),
      globalFilter,
      initialGlobalFilter,
      initialSelectedTags ? JSON.stringify(initialSelectedTags) : undefined,
    ] as const,
    queryFn: async () => {
      if (
        initialGlobalFilter !== undefined &&
        initialGlobalFilter !== globalFilter
      ) {
        setGlobalFilter(initialGlobalFilter);
      }

      if (
        initialSelectedTags !== undefined &&
        JSON.stringify(initialSelectedTags) !== JSON.stringify(selectedTags)
      ) {
        setSelectedTags(initialSelectedTags);
      }

      const response = await listFiles({
        query: {
          scope_id,
          scope_type,
          search: globalFilter,
          tags: selectedTags
            .map((v) => v.value)
            .filter((value): value is string => typeof value === 'string'),
          ownerId: claim?.owner?.id,
        },
      });

      return {
        files: filterDocumentsByVisibility({
          files: response.files,
          ownerId: claim?.owner?.id,
          visibility,
        }),
      };
    },
    initialData,
    keepPreviousData: true,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const stableFiles = useMemo(() => {
    if (!filesQuery.data?.files) return [];

    return filesQuery.data.files.map((file) => ({
      ...file,
      _version: Date.now() + Math.random(),
    }));
  }, [filesQuery.data?.files]);

  const untaggedFileIds = useMemo(() => {
    if (!filesQuery.data?.files) return [];

    return filesQuery.data.files
      .filter((file) => file.tags.length === 0)
      .map((file) => file.id);
  }, [filesQuery.data?.files]);

  const tagCheckQuery = useQuery({
    queryKey: [
      'tag-check-query',
      scope_id,
      scope_type,
      untaggedFileIds.join(','),
    ],
    queryFn: async () => {
      if (untaggedFileIds.length === 0) {
        return { files: [] };
      }

      setIsRefetching(untaggedFileIds.length > 0);

      const response = await listFiles({
        query: {
          scope_id,
          scope_type,
          ownerId: claim?.owner?.id,
        },
      });

      return {
        files: response.files.filter((file) =>
          untaggedFileIds.includes(file.id),
        ),
      };
    },
    enabled: untaggedFileIds.length > 0,
    refetchInterval: untaggedFileIds.length > 0 ? 3000 : false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 2000,
    onSuccess: (data) => {
      if (data?.files && data.files.length > 0) {
        const taggedFiles = data.files.filter((file) => file.tags.length > 0);

        if (taggedFiles.length > 0) {
          if (
            isRefetching &&
            untaggedFileIds.length === taggedFiles.length &&
            filesQuery.data?.files?.length > 0
          ) {
            setIsRefetching(false);
            queryClient.invalidateQueries({
              queryKey: [DOCUMENTATION_FILES_QUERY_KEY],
            });
            queryClient.invalidateQueries({
              queryKey: [SINGLE_FILE_QUERY_KEY],
            });
          }
        }
      }
    },
  });

  const { data: pendingFileRequest } = useQuery({
    queryKey: [buildClaimPendingFileRequestQueryKey(scope_id)],
    queryFn: () =>
      listPendingFileRequest({
        relationType: 'claim',
        relationTypeId: scope_id,
      }),
    refetchOnMount: true,
    initialData: initialPendingFileRequests,
    enabled: !!initialPendingFileRequests,
    refetchOnWindowFocus: false,
  });

  const pendingFileRequests = useMemo(() => {
    return pendingFileRequest &&
      pendingFileRequest.exists &&
      pendingFileRequest.file_requests
      ? (pendingFileRequest.file_requests as FileRequestWithRelations[])
      : [];
  }, [pendingFileRequest]);

  const updateSearchQuery = useCallback(
    debounce((value: string) => {
      setGlobalFilter(value);

      if (onSearch) {
        onSearch(value);
      }
    }, 300),
    [onSearch],
  );

  const FileListMemoized = useMemo(
    () => (
      <FileCardList className='mt-2'>
        {stableFiles.map((file) => (
          <MemoizedFileCard
            key={file.id}
            file={file}
            allFiles={stableFiles}
            onOpenFile={onOpenFile}
          />
        ))}

        {tokenizeFile && showPlaceholder && (
          <UploadFileCustom
            tokenizeFile={(input) => tokenizeFile(input)}
            classNames={{
              input: 'h-[12.8rem] border-slate-300',
            }}
            addFiles={addUntaggedFiles}
          />
        )}
      </FileCardList>
    ),
    [
      stableFiles,
      tokenizeFile,
      addUntaggedFiles,
      onOpenFile,
      searchTags,
      showPlaceholder,
    ],
  );

  const dedupedPendingPlaceholders = useMemo(() => {
    const placeholdersByTag = new Map<
      string,
      {
        fileRequestId: string;
        placeholder: any;
      }
    >();

    pendingFileRequests.forEach((fileRequest: any) => {
      fileRequest.placeholders.forEach((placeholder: any) => {
        const dedupeKey =
          placeholder.tag?.name?.trim().toLocaleLowerCase() ||
          placeholder.tag_id;
        const existing = placeholdersByTag.get(dedupeKey);
        if (!existing) {
          placeholdersByTag.set(dedupeKey, {
            fileRequestId: fileRequest.id,
            placeholder,
          });
          return;
        }

        const existingIsCancelled = !!existing.placeholder.deleted_at;
        const currentIsActive = !placeholder.deleted_at;

        // Prioritize active placeholders over cancelled ones.
        if (existingIsCancelled && currentIsActive) {
          placeholdersByTag.set(dedupeKey, {
            fileRequestId: fileRequest.id,
            placeholder,
          });
        }
      });
    });

    return Array.from(placeholdersByTag.values());
  }, [pendingFileRequests]);

  return (
    <div className='flex flex-col'>
      <div
        className={cn(
          'flex flex-row items-start justify-between space-x-2 w-full',
        )}
      >
        {title && (
          <div className='flex items-center justify-start h-8'>
            <h1 className='text-foreground text-sm font-medium'>{title}</h1>
          </div>
        )}

        <div className='flex flex-col items-start justify-end'>
          {!hideSearch && (
            <div className='flex flex-row items-center space-x-2'>
              <Input
                onChange={(e) => updateSearchQuery(e.target.value)}
                placeholder='Buscar'
                className='h-8'
                defaultValue={globalFilter}
              />
              <DocumentTagsFilter
                searchTags={searchTags}
                setSelectedValues={(values) => {
                  setSelectedTags(values);

                  if (onFilterChange) {
                    onFilterChange({
                      selectedTags: values,
                    });
                  }
                }}
                selectedValues={selectedTags}
              />
            </div>
          )}
        </div>
      </div>

      {withFileRequestButton && userType !== 'inhouse_lawyer' && (
        <div className='flex flex-col xl:flex-row xl:justify-between xl:items-center gap-2 my-2 w-full'>
          <p className='text-sm text-foreground font-medium'>
            Documentación subida por el tercero o reclamante.
          </p>
          <Link
            href={`/sinister/${claim?.sinister_id}/claim/${claim?.id}/documents/request-documents`}
            className={
              claim?.system_status === 'closed' ? 'pointer-events-none' : ''
            }
          >
            <Button
              type='button'
              className='h-8 truncate px-[.625rem] py-2 !text-base_brand rounded-xl'
              disabled={claim?.system_status === 'closed'}
            >
              Solicitar documentación
            </Button>
          </Link>
        </div>
      )}

      {filesQuery.data?.files?.length === 0 && (
        <DocumentsEmptyState
          initialPendingFileRequests={initialPendingFileRequests}
          tokenizeFile={tokenizeFile}
          addUntaggedFiles={addUntaggedFiles}
          userType={userType}
          showPlaceholder={showPlaceholder}
        />
      )}

      {filesQuery.data?.files?.length > 0 && FileListMemoized}

      {initialPendingFileRequests?.exists &&
        pendingFileRequests.length > 0 &&
        userType !== 'inhouse_lawyer' && (
          <div className={'mt-6'}>
            <div className='flex items-center justify-start h-8 mb-2'>
              <h1 className='text-foreground text-sm font-medium'>
                Documentación pendiente
              </h1>
            </div>
            <div className={'flex flex-wrap md:flex-row gap-2'}>
              {dedupedPendingPlaceholders.map(
                ({ fileRequestId, placeholder }) => {
                  const isCancelled = !!placeholder.deleted_at;
                  const showCancelAction =
                    !isCancelled &&
                    userType === 'insurer' &&
                    !!onCancelPlaceholder &&
                    !placeholder.dirty;

                  return (
                    <div
                      className={cn(
                        'cursor-default flex items-center justify-center rounded-xl text-base_brand border border-primary py-1 px-2 h-6 gap-1',
                        isCancelled && 'opacity-50 line-through',
                      )}
                      key={placeholder.id}
                    >
                      {isCancelled ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='text-base_brand text-foreground font-medium'>
                                {placeholder.tag?.name} ({t('cancelled')})
                              </span>
                            </TooltipTrigger>
                            <TooltipPortal>
                              <TooltipContent className='text-xs'>
                                {placeholder.deleted_reason
                                  ? `${t('reason')}: ${
                                      placeholder.deleted_reason
                                    }`
                                  : t('cancelled')}
                                <br />
                                <span className='text-xs text-muted-foreground'>
                                  Cancelado el{' '}
                                  {localeDateFormat(
                                    new Date(placeholder.deleted_at),
                                  )}
                                </span>
                              </TooltipContent>
                            </TooltipPortal>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className='text-base_brand text-foreground font-medium'>
                          {placeholder.tag?.name}
                        </span>
                      )}
                      {showCancelAction && (
                        <Button
                          type='button'
                          size='icon'
                          variant='ghost'
                          className='h-4 w-4 p-0 hover:bg-transparent'
                          onClick={() =>
                            onCancelPlaceholder({
                              id: placeholder.id,
                              fileRequestId,
                              tagName: placeholder.tag?.name || '',
                            })
                          }
                        >
                          <IconX className='h-3 w-3 text-red-600' />
                        </Button>
                      )}
                    </div>
                  );
                },
              )}
            </div>
          </div>
        )}

      {initialPendingFileRequests?.exists && userType === 'inhouse_lawyer' && (
        <section className='mt-6'>
          <div className='flex items-center justify-start h-8 mb-6'>
            <h1 className='text-foreground text-sm font-medium'>
              Documentación pendiente
            </h1>
          </div>
          <FileRequestMessage
            className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 space-y-0 mb-6'
            fileRequests={pendingFileRequests}
            userType={userType}
          />
        </section>
      )}
    </div>
  );
};
